import { useAtomValue } from 'jotai';
import { useCallback, useEffect, useState } from 'react';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogHeader,
  Skeleton,
  toaster,
} from '@optiaxiom/react';

import { opalAuthAtom } from '@frontend/atoms';
// import { ShadowDomContainer } from '@frontend/components/ShadowDomContainer/ShadowDomContainer';
import { CMP_BASE_URL } from '@frontend/envConfig';
import { getOrRenewOptiIdToken } from '@frontend/oktaAuth';

import { useCmpModules } from '../hooks/useCmpModules';
import type { HitlTaskDialogState } from '../utils';

type HitlTaskDialogProps = {
  onClose: () => void;
  state: HitlTaskDialogState;
};

export function HitlTaskDialog({ state, onClose }: HitlTaskDialogProps) {
  const { CmpEmbedProvider, EmbeddableTask, isLoaded, error } = useCmpModules(!!state);
  const opalAuth = useAtomValue(opalAuthAtom);
  const orgSsoId = opalAuth?.getAuthData()?.cmpOrgSsoId ?? '';
  const [token, setToken] = useState<string | null>(null);
  const [tokenError, setTokenError] = useState(false);

  useEffect(() => {
    if (!state) {
      setToken(null);
      setTokenError(false);
      return;
    }
    getOrRenewOptiIdToken()
      .then(setToken)
      .catch(() => setTokenError(true));
  }, [state]);

  const handleTokenExpired = useCallback(() => {
    getOrRenewOptiIdToken()
      .then(setToken)
      .catch(() => setTokenError(true));
  }, []);

  const handleApprove = useCallback(() => {
    onClose();
    toaster.create('Task approved', { intent: 'success' });
  }, [onClose]);

  const handleReject = useCallback(() => {
    onClose();
    toaster.create('Task rejected', { intent: 'information' });
  }, [onClose]);

  const title = state?.agentName ? `${state.agentName} — Approval Required` : 'Approval Required';

  const showContent = isLoaded && token && state && CmpEmbedProvider && EmbeddableTask;

  return (
    <Dialog onOpenChange={open => !open && onClose()} open={!!state}>
      <DialogContent size="lg">
        <DialogHeader>{title}</DialogHeader>
        <DialogBody overflow="hidden">
          {error || tokenError ? (
            <div>Failed to load task. Please try again.</div>
          ) : !isLoaded ? (
            // CMP embed (MFECmp remote) is currently disabled.
            <div>Task review is temporarily unavailable.</div>
          ) : showContent ? (
            <CmpEmbedProvider
              baseUrl={CMP_BASE_URL}
              onTokenExpired={handleTokenExpired}
              orgSsoId={orgSsoId}
              token={token}
            >
              <EmbeddableTask
                onApprove={handleApprove}
                onReject={handleReject}
                taskId={state.taskId}
              />
            </CmpEmbedProvider>
          ) : (
            <Skeleton h="384" w="full" />
          )}
        </DialogBody>
      </DialogContent>
    </Dialog>
  );
}
